import type { JSX } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import {
  type BubblePlacement,
  writeStoredPlacement,
} from '../services/widget-position-store';
import { draggedBubblePos, widgetClientId } from '../store/widget-store';

/** 长按多久(ms)进入「可拖」态 */
export const LONG_PRESS_MS = 450;
/** 长按触发前指针位移超过此值(px)则取消长按(视为滑动/滚动,不劫持点击) */
export const MOVE_CANCEL_PX = 10;
/** 拖动落点距视口边的最小留白(px),保证按钮整体可见 */
export const VIEWPORT_MARGIN = 8;
/** 拖动结束后拦截浏览器合成 click 的时间窗口(ms) */
export const DRAG_CLICK_GUARD_MS = 500;

/** web 面板标称尺寸(镜像 web-widget.css .dcs-web-panel 的 width/height),用于面板跟随时的 clamp 上界 */
export const WEB_PANEL_W = 400;
export const WEB_PANEL_H = 600;
/** web 面板跟随时距视口边的最小留白(px) */
export const WEB_PANEL_MARGIN = 8;

/** 迷你按钮 inline style:四边定位值(已带单位或 'auto'),兼容 preact style 属性 */
export type BubbleStyle = Pick<JSX.CSSProperties, 'left' | 'right' | 'top' | 'bottom'>;

/**
 * 把 left/top(视口坐标) clamp 进可视区,保证 w×h 的按钮整体可见。纯函数,便于单测。
 */
export function clampToViewport(
  left: number,
  top: number,
  w: number,
  h: number,
  vw: number,
  vh: number,
  margin: number,
): { left: number; top: number } {
  const maxLeft = Math.max(margin, vw - w - margin);
  const maxTop = Math.max(margin, vh - h - margin);
  return {
    left: Math.min(Math.max(left, margin), maxLeft),
    top: Math.min(Math.max(top, margin), maxTop),
  };
}

/**
 * 由按钮矩形(视口坐标)算就近角锚定 placement:中心落在哪半屏就锚哪边,
 * 偏移取距该锚定边的 px。纯函数,便于单测。
 */
export function computeAnchoredPlacement(
  rect: { left: number; top: number; width: number; height: number },
  vw: number,
  vh: number,
): BubblePlacement {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const ax: BubblePlacement['ax'] = cx <= vw / 2 ? 'left' : 'right';
  const ay: BubblePlacement['ay'] = cy <= vh / 2 ? 'top' : 'bottom';
  const x = ax === 'left' ? rect.left : vw - rect.left - rect.width;
  const y = ay === 'top' ? rect.top : vh - rect.top - rect.height;
  return { ax, ay, x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) };
}

/**
 * placement → inline style:只设锚定的两边,另两边 'auto'
 * (覆盖 .dcs-*-bubble 类里的 right/bottom: var(--dcs-offset-*),让缓存位置生效)。纯函数。
 */
export function placementToStyle(p: BubblePlacement): BubbleStyle {
  return {
    left: p.ax === 'left' ? `${p.x}px` : 'auto',
    right: p.ax === 'right' ? `${p.x}px` : 'auto',
    top: p.ay === 'top' ? `${p.y}px` : 'auto',
    bottom: p.ay === 'bottom' ? `${p.y}px` : 'auto',
  };
}

/**
 * 由按钮落点 placement 算 web 面板(普通停靠态)的 inline style:面板锚定到与按钮相同的角,
 * 偏移用按钮的 x/y,但因面板远大于按钮,须按面板尺寸 clamp 防溢出。
 *
 * 用 CSS clamp() 字符串表达(免读 window、纯函数可测、resize 自动重算):
 * 下界 margin、上界 calc(100vw/vh - 面板尺寸 - margin);极小视口下 clamp 取下界 margin,
 * 叠加 .dcs-web-panel 既有 max-width/height 兜底,不溢出。只设锚定两边,另两边 'auto'。
 */
export function computePanelStyle(p: BubblePlacement): BubbleStyle {
  const cx = `clamp(${WEB_PANEL_MARGIN}px, ${p.x}px, calc(100vw - ${WEB_PANEL_W}px - ${WEB_PANEL_MARGIN}px))`;
  const cy = `clamp(${WEB_PANEL_MARGIN}px, ${p.y}px, calc(100vh - ${WEB_PANEL_H}px - ${WEB_PANEL_MARGIN}px))`;
  return {
    left: p.ax === 'left' ? cx : 'auto',
    right: p.ax === 'right' ? cx : 'auto',
    top: p.ay === 'top' ? cy : 'auto',
    bottom: p.ay === 'bottom' ? cy : 'auto',
  };
}

/**
 * 迷你按钮「长按拖动」hook —— Pointer Events + Touch Events 兼容实现。
 *
 * 交互:按下后启动 LONG_PRESS_MS 定时器,期间移动超 MOVE_CANCEL_PX 取消长按(普通点击/滑动);
 * 定时器触发进入「可拖」态,后续 move 跟手移动并 clamp。触屏同时派发 pointer/touch 时由 touch
 * 接管同一手势,兼容 Pointer Events 支持不完整的微信 WebView。
 * 落手若发生过拖动:算就近角锚定 placement → 写 draggedBubblePos 信号 + localStorage,
 * 并在短时间窗内吞掉随后的合成 click(不误触打开);纯点击则照常调 onOpen。
 *
 * 返回的 style:拖动中取实时 left/top;否则取缓存 placement(draggedBubblePos)生成;无缓存为 undefined
 * (回落 CSS 类的服务端配置位置)。
 */
export function useBubbleDrag(onOpen: () => void) {
  const [dragging, setDragging] = useState(false);
  const [live, setLive] = useState<{ left: number; top: number } | null>(null);

  const timer = useRef<number | null>(null);
  const armed = useRef(false);
  const didDrag = useRef(false);
  const lastDraggedAt = useRef<number | null>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const grabDX = useRef(0);
  const grabDY = useRef(0);
  const btnW = useRef(0);
  const btnH = useRef(0);
  const inputSource = useRef<'pointer' | 'touch' | null>(null);
  const targetEl = useRef<HTMLElement | null>(null);
  const pointerId = useRef(-1);
  const touchId = useRef(-1);

  const clearTimer = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const begin = (el: HTMLElement, clientX: number, clientY: number) => {
    const rect = el.getBoundingClientRect();
    targetEl.current = el;
    startX.current = clientX;
    startY.current = clientY;
    grabDX.current = clientX - rect.left;
    grabDY.current = clientY - rect.top;
    btnW.current = rect.width;
    btnH.current = rect.height;
    armed.current = false;
    didDrag.current = false;
    clearTimer();
    timer.current = window.setTimeout(() => {
      armed.current = true;
      setDragging(true);
      setLive({ left: rect.left, top: rect.top });
    }, LONG_PRESS_MS);
  };

  const move = (clientX: number, clientY: number, preventDefault: () => void) => {
    if (!armed.current) {
      const dx = Math.abs(clientX - startX.current);
      const dy = Math.abs(clientY - startY.current);
      if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) clearTimer();
      return;
    }
    preventDefault();
    didDrag.current = true;
    const { left, top } = clampToViewport(
      clientX - grabDX.current,
      clientY - grabDY.current,
      btnW.current,
      btnH.current,
      window.innerWidth,
      window.innerHeight,
      VIEWPORT_MARGIN,
    );
    setLive({ left, top });
  };

  const finish = (el: HTMLElement) => {
    clearTimer();
    if (armed.current && didDrag.current) {
      const rect = el.getBoundingClientRect();
      const placement = computeAnchoredPlacement(
        rect,
        window.innerWidth,
        window.innerHeight,
      );
      draggedBubblePos.value = placement;
      writeStoredPlacement(widgetClientId.value, placement);
      lastDraggedAt.current = Date.now();
    }
    inputSource.current = null;
    targetEl.current = null;
    pointerId.current = -1;
    touchId.current = -1;
    armed.current = false;
    didDrag.current = false;
    setDragging(false);
    setLive(null);
  };

  const cancel = () => {
    clearTimer();
    inputSource.current = null;
    targetEl.current = null;
    pointerId.current = -1;
    touchId.current = -1;
    armed.current = false;
    didDrag.current = false;
    setDragging(false);
    setLive(null);
  };

  const onPointerDown = useCallback((e: PointerEvent) => {
    if (e.button !== 0 || inputSource.current !== null) return;
    const el = e.currentTarget as HTMLElement;
    inputSource.current = 'pointer';
    pointerId.current = e.pointerId;
    // 部分 WebView 只允许在 pointerdown 调用捕获,不能等长按定时器触发后再调用。
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* jsdom / 不支持 PointerCapture 时忽略 */
    }
    begin(el, e.clientX, e.clientY);
  }, []);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (inputSource.current !== 'pointer' || e.pointerId !== pointerId.current) return;
    move(e.clientX, e.clientY, () => e.preventDefault());
  }, []);

  const onPointerUp = useCallback((e: PointerEvent) => {
    if (inputSource.current !== 'pointer' || e.pointerId !== pointerId.current) return;
    const el = e.currentTarget as HTMLElement;
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    finish(el);
  }, []);

  const onPointerCancel = useCallback((e: PointerEvent) => {
    if (inputSource.current !== 'pointer' || e.pointerId !== pointerId.current) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    cancel();
  }, []);

  const onTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!touch) return;

    if (inputSource.current === 'pointer') {
      // 标准触屏会先发 pointerdown 再发 touchstart。改由 touch 路径继续同一手势,
      // 这样微信 WebView 即使后续 pointermove/pointerup 不完整也不会中断拖动。
      try {
        targetEl.current?.releasePointerCapture(pointerId.current);
      } catch {
        /* ignore */
      }
      inputSource.current = 'touch';
      pointerId.current = -1;
      touchId.current = touch.identifier;
      return;
    }
    if (inputSource.current !== null) return;

    inputSource.current = 'touch';
    touchId.current = touch.identifier;
    begin(e.currentTarget as HTMLElement, touch.clientX, touch.clientY);
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (inputSource.current !== 'touch') return;
    const touch = Array.from(e.touches).find((item) => item.identifier === touchId.current);
    if (!touch) return;
    move(touch.clientX, touch.clientY, () => e.preventDefault());
  }, []);

  const onTouchEnd = useCallback((e: TouchEvent) => {
    if (inputSource.current !== 'touch') return;
    const ended = Array.from(e.changedTouches).some(
      (item) => item.identifier === touchId.current,
    );
    if (!ended) return;
    if (armed.current && didDrag.current) e.preventDefault();
    finish(e.currentTarget as HTMLElement);
  }, []);

  const onTouchCancel = useCallback((e: TouchEvent) => {
    if (inputSource.current !== 'touch') return;
    const cancelled = Array.from(e.changedTouches).some(
      (item) => item.identifier === touchId.current,
    );
    if (cancelled) cancel();
  }, []);

  const onClick = useCallback(
    (e: MouseEvent) => {
      const draggedAt = lastDraggedAt.current;
      lastDraggedAt.current = null;
      if (draggedAt !== null && Date.now() - draggedAt <= DRAG_CLICK_GUARD_MS) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onOpen();
    },
    [onOpen],
  );

  const placement = draggedBubblePos.value;
  const style: BubbleStyle | undefined =
    dragging && live
      ? { left: `${live.left}px`, top: `${live.top}px`, right: 'auto', bottom: 'auto' }
      : placement
        ? placementToStyle(placement)
        : undefined;

  return {
    dragging,
    style,
    onClick,
    pointerHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
    },
  };
}
